/*!
 * Tec Vanlife Chat Widget — embeddable, Shadow-DOM-isolated chat launcher.
 *
 * Context-aware: on each send it detects the current Shopify page (home /
 * collection / product) and forwards product_id / collection_id so the backend
 * PRIORITIZES the product or collection the shopper is viewing.
 *
 * Install (paste before </body> in layout/theme.liquid):
 *   <script src="https://your-host/tecvan-chat-widget.js"
 *           data-api-url="https://your-lambda-url/"
 *           data-bot-id="tecvan"
 *           data-bot-name="Tec Vanlife Assistant"
 *           data-store-domain="{{ shop.permanent_domain }}"
 *           data-brand-color="#e86d29"
 *           data-welcome-message="Hi! Ask me about our camper-van parts..."
 *           data-avatar-label="TV"
 *           defer></script>
 *
 * Or configure via window.TecvanChatConfig before loading the script:
 *   <script>window.TecvanChatConfig = { apiUrl: "https://your-lambda-url/" };</script>
 *   <script src="https://your-host/tecvan-chat-widget.js" defer></script>
 *
 * Programmatic control after load:
 *   window.TecvanChat.open();  .close();  .toggle();  .reset();
 */
(function () {
  "use strict";

  var WIDGET_VERSION = "1.1.0";

  // Capture currentScript synchronously (null-safe inside async callbacks).
  var scriptEl = document.currentScript;

  function uuid() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  // SHA-256 hex of a string's UTF-8 bytes — the body hash CloudFront OAC needs
  // for signed POSTs (sent as x-amz-content-sha256).
  async function sha256Hex(str) {
    var bytes = new TextEncoder().encode(str);
    var digest = await crypto.subtle.digest("SHA-256", bytes);
    return Array.prototype.map
      .call(new Uint8Array(digest), function (b) { return ("0" + b.toString(16)).slice(-2); })
      .join("");
  }

  // Lightweight device fingerprint — a stable hash of coarse device/browser
  // signals (no external library, no cross-site tracking). Cached per page load.
  var _deviceFp = null;
  async function deviceFingerprint() {
    if (_deviceFp != null) return _deviceFp;
    try {
      var n = navigator, s = window.screen || {};
      var parts = [
        n.userAgent, n.language, (n.languages || []).join(","), n.platform,
        n.hardwareConcurrency, n.deviceMemory,
        s.width + "x" + s.height + "x" + s.colorDepth, window.devicePixelRatio,
        (Intl.DateTimeFormat().resolvedOptions() || {}).timeZone, new Date().getTimezoneOffset()
      ];
      _deviceFp = (await sha256Hex(parts.join("|"))).slice(0, 32);
    } catch (e) { _deviceFp = ""; }
    return _deviceFp;
  }

  function readConfig() {
    var userConfig = window.TecvanChatConfig || {};
    var dataConfig = {};
    if (scriptEl && scriptEl.dataset) {
      var d = scriptEl.dataset;
      if (d.apiUrl) dataConfig.apiUrl = d.apiUrl;
      if (d.feedbackUrl) dataConfig.feedbackUrl = d.feedbackUrl;
      if (d.botId) dataConfig.botId = d.botId;
      if (d.botName) dataConfig.botName = d.botName;
      if (d.welcomeMessage) dataConfig.welcomeMessage = d.welcomeMessage;
      if (d.avatarLabel) dataConfig.avatarLabel = d.avatarLabel;
      if (d.launcherLabel) dataConfig.launcherLabel = d.launcherLabel;
      if (d.inputPlaceholder) dataConfig.inputPlaceholder = d.inputPlaceholder;
      if (d.brandColor) dataConfig.brandColor = d.brandColor;
      if (d.brandColor2) dataConfig.brandColor2 = d.brandColor2;
      if (d.storeDomain) dataConfig.storeDomain = d.storeDomain;
    }
    var defaults = {
      apiUrl: "",
      feedbackUrl: "",
      botId: "tecvan",
      botName: "Tec Vanlife Assistant",
      welcomeMessage:
        "Hi! I'm the Tec Vanlife assistant. Ask me about our camper-van parts — fitment, specs, installation, shipping, or anything for your build.",
      avatarLabel: "TV",
      launcherLabel: "Chat with us",
      inputPlaceholder: "Ask about products, fitment, shipping...",
      brandColor: "#e86d29",  // Tec Vanlife logo orange
      brandColor2: "#f4924a", // lighter orange for gradients
      storeDomain: location.host
    };
    return Object.assign({}, defaults, userConfig, dataConfig);
  }

  function deriveFeedbackUrl(cfg) {
    if (cfg.feedbackUrl) return cfg.feedbackUrl;
    if (!cfg.apiUrl) return "";
    if (/\/chat\/?$/.test(cfg.apiUrl)) return cfg.apiUrl.replace(/\/chat(\/?)$/, "/feedback$1");
    return cfg.apiUrl.replace(/\/?$/, "") + "/feedback";
  }

  // ─── Shopify page-context detection ───
  // Tells the backend which page the shopper is on so it can PRIORITIZE the
  // current product / collection. Merges URL patterns with Shopify's globals.
  var _relatedCache = {};

  function detectPageContext(cfg) {
    var ctx = { scope: "other", path: location.pathname, title: document.title, page_url: location.href };
    var meta = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) || {};
    var pageType = meta.page && meta.page.pageType;
    if (meta.product) {
      if (meta.product.id) ctx.product_id = meta.product.id;
      if (meta.product.handle) ctx.product_handle = meta.product.handle;
    }
    if (meta.page && meta.page.resourceType === "collection" && meta.page.resourceId) {
      ctx.collection_id = meta.page.resourceId;
    }
    var p = location.pathname;
    var mProd = p.match(/\/products\/([^\/?#]+)/);
    var mCol = p.match(/\/collections\/([^\/?#]+)/);
    if (mProd) {
      ctx.scope = "product";
      if (!ctx.product_handle) ctx.product_handle = mProd[1];
      if (mCol) ctx.collection_handle = mCol[1];
    } else if (mCol) {
      ctx.scope = "collection";
      ctx.collection_handle = mCol[1];
    } else if (p === "/" || /^\/pages\//.test(p)) {
      ctx.scope = "home";
    }
    // Shopify globals confirm/correct the page type when available.
    if (pageType === "product") ctx.scope = "product";
    else if (pageType === "collection" && ctx.scope !== "product") ctx.scope = "collection";
    else if (pageType === "home") ctx.scope = "home";
    // Best-effort selected-variant SKU (for per-SKU log extraction later). The
    // ?variant= id matched against Shopify's product meta; silently skipped if
    // unavailable — the backend can still map product_handle -> SKUs.
    try {
      if (ctx.scope === "product" && meta.product && meta.product.variants) {
        var vid = new URLSearchParams(location.search).get("variant");
        var variants = meta.product.variants;
        var chosen = null;
        for (var i = 0; i < variants.length; i++) {
          if (vid && String(variants[i].id) === String(vid)) { chosen = variants[i]; break; }
        }
        if (!chosen) chosen = variants[0];
        if (chosen && chosen.sku) ctx.sku = chosen.sku;
      }
    } catch (e) { /* ignore */ }
    return ctx;
  }

  // Related-product handles from Shopify's PUBLIC recommendations endpoint, with
  // a same-collection fallback. Cached per product; never throws. Lazy (called
  // on send), short timeout, so it can't slow the page.
  function fetchRelated(ctx) {
    if (ctx.scope !== "product" || !ctx.product_id) return Promise.resolve([]);
    var key = String(ctx.product_id);
    if (_relatedCache[key]) return Promise.resolve(_relatedCache[key]);
    var ac = new AbortController();
    var timer = setTimeout(function () { ac.abort(); }, 1500);
    var recUrl = "/recommendations/products.json?product_id=" +
      encodeURIComponent(ctx.product_id) + "&intent=related&limit=10";
    return fetch(recUrl, { signal: ac.signal })
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .then(function (data) {
        var handles = (data.products || []).map(function (x) { return x.handle; }).filter(Boolean);
        if (handles.length > 0) return handles;
        if (!ctx.collection_handle) return [];
        return fetch("/collections/" + ctx.collection_handle + "/products.json?limit=10")
          .then(function (r2) { return r2.ok ? r2.json() : { products: [] }; })
          .then(function (d2) {
            return (d2.products || []).map(function (x) { return x.handle; }).filter(Boolean);
          });
      })
      .catch(function () { return []; })
      .then(function (handles) {
        clearTimeout(timer);
        _relatedCache[key] = handles;
        return handles;
      });
  }

  var FONT = "-apple-system, BlinkMacSystemFont, 'Helvetica Neue', Helvetica, Arial, sans-serif";
  var CSS = [
    // Match the chatbot (static/index.html + Next.js `antialiased` body) by
    // pinning the same font stack AND the same smoothing hints inside the
    // shadow DOM. `all: initial` strips inheritance, so re-declare smoothing
    // here or fonts render slightly bolder than the chatbot on macOS.
    ":host { all: initial; font-family: " + FONT + "; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; text-rendering: optimizeLegibility; }",
    // Force the font on every descendant — UA stylesheets reset font-family on form controls.
    ":host, :host *, button, input, textarea, select { font-family: " + FONT + " !important; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }",
    "* { box-sizing: border-box; }",
    ".btn {",
    "  position: fixed; right: 24px; bottom: 24px;",
    "  display: inline-flex; align-items: center; gap: 9px;",
    "  height: 52px; padding: 0 20px; border-radius: 26px;",
    "  border: none; cursor: pointer;",
    "  background: var(--tv-brand, #e86d29);",
    "  color: #fff;",
    "  box-shadow: 0 8px 24px rgba(26,26,26,0.16);",
    "  z-index: 2147483000;",
    "  transition: transform 0.15s ease;",
    "}",
    ".btn:active { transform: scale(0.96); }",
    ".btn-label {",
    "  font-size: 13px; font-weight: 600; letter-spacing: 0.06em;",
    "  text-transform: uppercase; white-space: nowrap;",
    "}",
    // Hide the launcher entirely while the panel is open — the panel header has
    // its own close button, so a second close affordance is redundant.
    ".btn.is-open { display: none; }",
    ".panel {",
    "  position: fixed; right: 24px; bottom: 24px;",
    "  width: min(380px, calc(100vw - 32px));",
    "  height: min(560px, calc(100vh - 48px));",
    "  background: #fff;",
    "  border-radius: 14px;",
    "  border: 1px solid #e6e4de;",
    "  box-shadow: 0 12px 48px rgba(26,26,26,0.12);",
    "  display: flex; flex-direction: column; overflow: hidden;",
    "  z-index: 2147483001;",
    "  color: #57564f;",
    "}",
    ".panel[hidden] { display: none; }",
    ".header {",
    "  padding: 14px 16px;",
    "  background: #fff;",
    "  color: #1a1a1a;",
    "  border-bottom: 1px solid #e6e4de;",
    "  display: flex; align-items: center; gap: 10px;",
    "}",
    ".header .avatar {",
    "  width: 34px; height: 34px; border-radius: 50%;",
    "  background: var(--tv-brand, #e86d29); color: #fff;",
    "  display: flex; align-items: center; justify-content: center;",
    "  font-size: 12px; font-weight: 700;",
    "}",
    ".header .title { flex: 1; min-width: 0; }",
    ".header .name { font-size: 15px; font-weight: 700; line-height: 1.2; }",
    ".header .status { font-size: 11px; color: #8a877e; margin-top: 2px; }",
    ".header .status-dot {",
    "  display: inline-block; width: 6px; height: 6px; border-radius: 50%;",
    "  background: #1bbc9d; margin-right: 6px; vertical-align: middle;",
    "}",
    ".header .close, .header .minimize {",
    "  background: transparent; border: none; color: #57564f;",
    "  opacity: 0.9; cursor: pointer; padding: 4px; display: flex;",
    "}",
    ".header .close:hover, .header .minimize:hover { opacity: 1; color: var(--tv-brand, #e86d29); }",
    ".messages {",
    "  flex: 1; overflow-y: auto;",
    "  padding: 14px 14px 6px;",
    "  display: flex; flex-direction: column; gap: 12px;",
    "  background: #f6f5f2;",
    "}",
    ".msg { display: flex; gap: 8px; align-items: flex-start; }",
    ".msg.user { justify-content: flex-end; }",
    ".avatar-sm {",
    "  width: 26px; height: 26px; border-radius: 50%;",
    "  background: var(--tv-brand, #e86d29);",
    "  color: #fff;",
    "  display: flex; align-items: center; justify-content: center;",
    "  font-size: 10px; font-weight: 700; flex-shrink: 0; margin-top: 2px;",
    "}",
    ".bubble {",
    "  max-width: 82%;",
    "  padding: 9px 12px;",
    "  font-size: 14px; line-height: 1.85;",
    "  font-family: " + FONT + ";",
    "  word-break: break-word;",
    "  white-space: pre-wrap;",
    "}",
    ".msg.user .bubble {",
    "  border-radius: 12px 12px 4px 12px;",
    "  background: var(--tv-brand, #e86d29); color: #fff;",
    "}",
    ".msg.bot .bubble {",
    "  border-radius: 12px 12px 12px 4px;",
    "  background: #fff; color: #57564f;",
    "  border: 1px solid #e6e4de;",
    "}",
    ".bubble strong { color: var(--tv-brand, #e86d29); font-weight: 700; }",
    ".bubble .md-h { display: block; font-weight: 700; color: #1a1a1a; margin: 8px 0 2px; line-height: 1.3; }",
    ".bubble .md-h1 { font-size: 1.15em; }",
    ".bubble .md-h2 { font-size: 1.07em; }",
    ".bubble .md-h3 { font-size: 1em; }",
    ".bubble code {",
    "  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;",
    "  background: #f6f5f2; border: 1px solid #e6e4de; border-radius: 4px;",
    "  padding: 1px 5px; font-size: 0.92em; color: var(--tv-brand, #e86d29);",
    "}",
    ".bubble a { color: var(--tv-brand, #e86d29); text-decoration: underline; }",
    ".typing {",
    "  background: #fff; border: 1px solid #e6e4de;",
    "  border-radius: 12px 12px 12px 4px;",
    "  padding: 10px 14px; display: flex; gap: 4px;",
    "}",
    ".typing span {",
    "  width: 6px; height: 6px; border-radius: 50%;",
    "  background: var(--tv-brand, #e86d29); animation: tvPulse 1.2s infinite;",
    "}",
    ".typing span:nth-child(2) { animation-delay: 120ms; }",
    ".typing span:nth-child(3) { animation-delay: 240ms; }",
    "@keyframes tvPulse {",
    "  0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }",
    "  30% { transform: translateY(-3px); opacity: 1; }",
    "}",
    ".input-row {",
    "  padding: 12px; background: #fff;",
    "  border-top: 1px solid #e6e4de;",
    "  display: flex; gap: 8px; align-items: flex-end;",
    "}",
    ".input-row textarea {",
    "  flex: 1; resize: none;",
    "  border: 1px solid #e6e4de; border-radius: 6px;",
    "  padding: 9px 12px; font-size: 14px;",
    "  font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Helvetica, Arial, sans-serif;",
    "  color: #57564f; outline: none;",
    "  max-height: 120px; line-height: 1.4;",
    "}",
    ".input-row .send {",
    "  width: 38px; height: 38px; border-radius: 50%; border: none;",
    "  background: var(--tv-brand, #e86d29);",
    "  color: #fff; cursor: pointer;",
    "  display: flex; align-items: center; justify-content: center; flex-shrink: 0;",
    "}",
    ".input-row .send:disabled { background: #cfcdc6; cursor: not-allowed; }",
    ".feedback-row {",
    "  display: flex; gap: 4px; align-items: center;",
    "  margin: 4px 0 0 34px;",
    "}",
    ".feedback-btn {",
    "  background: transparent; border: none; cursor: pointer;",
    "  padding: 4px; border-radius: 6px; line-height: 0;",
    "  color: #8a877e; transition: background 0.15s ease, color 0.15s ease;",
    "}",
    ".feedback-btn.up:hover:not(:disabled) { background: #e6f7f0; color: #2bb673; }",
    ".feedback-btn.down:hover:not(:disabled) { background: #fdeceb; color: #e15554; }",
    ".feedback-btn.up.active { color: #2bb673; }",
    ".feedback-btn.down.active { color: #e15554; }",
    ".feedback-btn:disabled { cursor: default; }",
    ".feedback-btn.active:disabled { opacity: 1; }",
    ".feedback-thanks { font-size: 11px; color: #8a877e; margin-left: 4px; }",
    ".conv-feedback {",
    "  margin: 12px 0 4px 34px; padding: 10px 12px;",
    "  background: #f6f5f2; border: 1px solid #e6e4de; border-radius: 10px;",
    "  display: flex; align-items: center; justify-content: space-between; gap: 10px;",
    "}",
    ".conv-feedback.done { justify-content: flex-start; }",
    ".conv-feedback-q { font-size: 13px; font-weight: 600; color: #1a1a1a; }",
    ".conv-feedback-thanks { font-size: 13px; color: #57564f; }",
    ".conv-feedback-btns { display: flex; gap: 6px; flex-shrink: 0; }",
    ".conv-feedback-btn {",
    "  background: #fff; border: 1px solid #d8d6cf; cursor: pointer;",
    "  padding: 6px 12px; border-radius: 8px; font-size: 13px; font-weight: 600;",
    "  color: #57564f; transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;",
    "  font-family: inherit;",
    "}",
    ".conv-feedback-btn.up:hover { background: #e6f7f0; color: #2bb673; border-color: #2bb673; }",
    ".conv-feedback-btn.down:hover { background: #fdeceb; color: #e15554; border-color: #e15554; }",
    ".support-row { margin: 2px 0 0 34px; }",
    ".support-link {",
    "  background: transparent; border: none; cursor: pointer; padding: 2px 0;",
    "  font-family: inherit; font-size: 11px; color: #8a877e; text-decoration: underline;",
    "}",
    ".support-link:hover { color: #e86d29; }",
    ".support-sent { font-size: 11px; color: #2bb673; }",
    ".support-form {",
    "  margin: 6px 0 0 34px; padding: 8px 10px;",
    "  background: #fff; border: 1px solid #e6e4de; border-radius: 8px;",
    "}",
    ".support-form-label { font-size: 12px; color: #57564f; margin-bottom: 6px; }",
    ".support-form-row { display: flex; gap: 6px; align-items: center; }",
    ".support-email-input {",
    "  flex: 1; min-width: 0; border: 1px solid #d8d6cf; border-radius: 6px;",
    "  padding: 6px 8px; font-family: inherit; font-size: 13px; color: #1a1a1a; outline: none;",
    "}",
    ".support-email-input:focus { border-color: #e86d29; }",
    ".support-btn {",
    "  background: #fff; border: 1px solid #d8d6cf; cursor: pointer;",
    "  padding: 6px 10px; border-radius: 6px; font-size: 12px; font-weight: 600;",
    "  color: #57564f; font-family: inherit;",
    "}",
    ".support-btn.primary { background: #e86d29; border-color: #e86d29; color: #fff; }",
    ".support-btn:disabled { opacity: 0.6; cursor: default; }",
    ".support-err { font-size: 11px; color: #e15554; margin-top: 4px; }",
    ".feedback-comment {",
    "  margin: 6px 0 0 34px;",
    "  display: flex; flex-direction: column; gap: 6px;",
    "  background: #fff; border: 1px solid #e6e4de; border-radius: 6px;",
    "  padding: 8px;",
    "}",
    ".feedback-comment textarea {",
    "  border: none; outline: none; resize: vertical;",
    "  min-height: 48px; max-height: 120px;",
    "  font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Helvetica, Arial, sans-serif;",
    "  font-size: 13px; color: #57564f;",
    "}",
    ".feedback-comment .row {",
    "  display: flex; gap: 6px; justify-content: flex-end;",
    "}",
    ".feedback-comment button {",
    "  font-size: 11px; padding: 6px 12px; border-radius: 4px;",
    "  border: 1px solid #e6e4de; background: #fff; color: #1a1a1a;",
    "  cursor: pointer; font-family: inherit;",
    "  text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600;",
    "}",
    ".feedback-comment button.primary {",
    "  background: var(--tv-brand, #e86d29);",
    "  border-color: transparent; color: #fff;",
    "}",
    ".footer {",
    "  padding: 6px 12px 8px; background: #fff;",
    "  border-top: 1px solid #ece9e3;",
    "  text-align: center;",
    "}",
    ".footer-link {",
    "  background: transparent; border: none; cursor: pointer;",
    "  font-size: 11px; color: #8a877e; text-decoration: underline;",
    "  font-family: inherit; padding: 2px 6px;",
    "}",
    ".footer-link:hover { color: var(--tv-brand, #e86d29); }",
    ".footer-support { font-size: 11px; color: #8a877e; margin-bottom: 5px; line-height: 1.4; }",
    ".footer-support a { color: var(--tv-brand, #e86d29); text-decoration: none; }",
    ".footer-support a:hover { text-decoration: underline; }",
    ".modal-overlay {",
    "  position: absolute; inset: 0;",
    "  background: rgba(26,26,26,0.30);",
    "  display: flex; align-items: center; justify-content: center;",
    "  padding: 16px; z-index: 10;",
    "}",
    ".modal-overlay[hidden] { display: none; }",
    ".modal {",
    "  background: #fff; border-radius: 12px; padding: 14px;",
    "  width: 100%; box-shadow: 0 8px 24px rgba(26,26,26,0.16);",
    "  display: flex; flex-direction: column; gap: 10px;",
    "}",
    ".modal h3 { margin: 0; font-size: 14px; color: #57564f; font-weight: 700; }",
    ".modal p { margin: 0; font-size: 12px; color: #8a877e; }",
    ".modal textarea {",
    "  border: 1px solid #e6e4de; border-radius: 8px; padding: 8px;",
    "  resize: vertical; min-height: 80px; max-height: 180px;",
    "  font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Helvetica, Arial, sans-serif;",
    "  font-size: 13px; color: #57564f; outline: none;",
    "}",
    ".modal .row { display: flex; gap: 8px; justify-content: flex-end; }",
    ".modal button {",
    "  font-size: 11px; padding: 7px 14px; border-radius: 4px;",
    "  border: 1px solid #e6e4de; background: #fff; color: #1a1a1a;",
    "  cursor: pointer; font-family: inherit;",
    "  text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600;",
    "}",
    ".modal button.primary {",
    "  background: var(--tv-brand, #e86d29);",
    "  border-color: transparent; color: #fff;",
    "}",
    ".figures {",
    "  margin-top: 10px; padding-top: 8px;",
    "  border-top: 1px dashed #e6e4de;",
    "  display: flex; flex-direction: column; gap: 8px;",
    "}",
    ".figures-label {",
    "  font-size: 9px; font-weight: 700; letter-spacing: 1.2px;",
    "  text-transform: uppercase; color: #8a877e;",
    "}",
    ".figure { display: flex; flex-direction: column; gap: 3px; }",
    ".figure a { display: block; line-height: 0; }",
    ".figure img {",
    "  display: block; width: 100%; height: auto;",
    "  max-height: 220px; object-fit: contain;",
    "  background: #f6f5f2;",
    "  border: 1px solid #e6e4de; border-radius: 6px;",
    "  cursor: zoom-in;",
    "}",
    ".figure-caption {",
    "  font-size: 11px; color: #8a877e; font-style: italic;",
    "  line-height: 1.35; word-break: break-word;",
    "}",
    ".pcards { display: flex; flex-direction: column; gap: 8px; margin-top: 10px; }",
    ".pcard { display: flex; gap: 10px; align-items: center; text-decoration: none;",
    "  border: 1px solid #e6e4de; border-radius: 6px; padding: 8px; background: #fff;",
    "  color: #57564f; transition: box-shadow 0.15s ease; }",
    ".pcard:hover { box-shadow: 0 4px 14px rgba(0,0,0,0.10); }",
    ".pcard-img { width: 52px; height: 52px; border-radius: 8px; object-fit: cover;",
    "  flex: 0 0 auto; background: #f0eee9; }",
    ".pcard-body { display: flex; flex-direction: column; gap: 2px; min-width: 0; }",
    ".pcard-title { font-size: 13px; font-weight: 600; line-height: 1.25; }",
    ".pcard-price { font-size: 12px; color: var(--tv-brand, #e86d29); font-weight: 600; }",
    ".pcard-cta { font-size: 11px; color: var(--tv-brand, #e86d29); }"
  ].join("\n");

  var TEMPLATE = [
    '<button class="btn" type="button" aria-label="Open chat">',
    '  <svg class="icon-chat" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">',
    '    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>',
    '  </svg>',
    '  <svg class="icon-close" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" style="display:none;">',
    '    <line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/>',
    '  </svg>',
    '  <span class="btn-label"></span>',
    '</button>',
    '<div class="panel" role="dialog" aria-label="Chat" hidden>',
    '  <div class="header">',
    '    <div class="avatar"></div>',
    '    <div class="title">',
    '      <div class="name"></div>',
    '      <div class="status"><span class="status-dot"></span>Online</div>',
    '    </div>',
    '    <button class="minimize" type="button" aria-label="Minimize (keep chat)" title="Minimize — keep this conversation">',
    '      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round">',
    '        <line x1="6" y1="12" x2="18" y2="12"/>',
    '      </svg>',
    '    </button>',
    '    <button class="close" type="button" aria-label="Close (end chat)" title="Close — end this conversation">',
    '      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round">',
    '        <line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/>',
    '      </svg>',
    '    </button>',
    '  </div>',
    '  <div class="messages"></div>',
    '  <div class="input-row">',
    '    <textarea rows="1" placeholder=""></textarea>',
    '    <button class="send" type="button" aria-label="Send" disabled>',
    '      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">',
    '        <line x1="22" y1="2" x2="11" y2="13"/>',
    '        <polygon points="22 2 15 22 11 13 2 9 22 2"/>',
    '      </svg>',
    '    </button>',
    '  </div>',
    '  <div class="footer">',
    '    <div class="footer-support">Contact <a href="mailto:support@tecvan.com">support@tecvan.com</a> if you need to chat with the team.</div>',
    '    <button type="button" class="footer-link general-feedback-btn">Send feedback about this bot</button>',
    '  </div>',
    '  <div class="modal-overlay" hidden>',
    '    <div class="modal" role="dialog" aria-label="Send feedback">',
    '      <h3>Share feedback</h3>',
    '      <p>Tell us what worked, what didn\'t, or anything you\'d like to see improved.</p>',
    '      <textarea class="modal-textarea" placeholder="Your feedback..."></textarea>',
    '      <div class="row">',
    '        <button type="button" class="modal-cancel">Cancel</button>',
    '        <button type="button" class="modal-send primary">Send</button>',
    '      </div>',
    '    </div>',
    '  </div>',
    '</div>'
  ].join("\n");

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function stripAttachTags(s) {
    return String(s).replace(/\[ATTACH:[^\]]*\]/g, "").replace(/\n{3,}/g, "\n\n").trim();
  }

  function renderInline(text) {
    var html = escapeHtml(text);
    // ATX headings (#..###### on their own line) -> a bold heading line, sized
    // by level. Done before bold/link so heading content still gets those.
    html = html.replace(/^ {0,3}(#{1,6})[ \t]+([^\n]+?)[ \t]*#*[ \t]*(?:\n|$)/gm, function (_, hashes, content) {
      var level = Math.min(hashes.length, 3);
      return '<span class="md-h md-h' + level + '">' + content + "</span>";
    });
    // Bullet markers at line start -> a bullet glyph (keeps the line via pre-wrap).
    html = html.replace(/^ {0,3}[-*] +/gm, "• ");
    html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    html = html.replace(/`([^`]+)`/g, "<code>$1</code>");
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, function (_, t, u) {
      var safeUrl = /^(https?:|mailto:|\/)/i.test(u) ? u : "#";
      return '<a href="' + escapeHtml(safeUrl) + '" target="_blank" rel="noopener noreferrer">' + t + "</a>";
    });
    return html;
  }

  function renderProductsHtml(products) {
    if (!products || products.length === 0) return "";
    var cards = products.slice(0, 3).map(function (p) {
      if (!p) return "";
      var href = escapeHtml(p.url || (p.handle ? "/products/" + p.handle : "#"));
      var img = p.image
        ? '<img class="pcard-img" src="' + escapeHtml(p.image) + '" alt="" />'
        : '<div class="pcard-img pcard-noimg"></div>';
      var price = p.price ? '<div class="pcard-price">' + escapeHtml(p.price) + "</div>" : "";
      return (
        '<a class="pcard" href="' + href + '">' +
        img +
        '<div class="pcard-body">' +
        '<div class="pcard-title">' + escapeHtml(p.title || p.handle || "Product") + "</div>" +
        price +
        '<div class="pcard-cta">View product &rarr;</div>' +
        "</div></a>"
      );
    }).join("");
    return '<div class="pcards">' + cards + "</div>";
  }

  function renderFiguresHtml(figures) {
    if (!figures || figures.length === 0) return "";
    var items = figures
      .map(function (f) {
        if (!f || !f.url) return "";
        var url = escapeHtml(f.url);
        var alt = escapeHtml(f.caption || "");
        var captionParts = [];
        if (f.caption) captionParts.push(escapeHtml(f.caption));
        if (f.source_title) captionParts.push("— " + escapeHtml(f.source_title));
        var caption = captionParts.join(" ");
        return (
          '<div class="figure">' +
          '<a href="' + url + '" target="_blank" rel="noopener noreferrer">' +
          '<img src="' + url + '" alt="' + alt + '" loading="lazy" />' +
          "</a>" +
          (caption ? '<div class="figure-caption">' + caption + "</div>" : "") +
          "</div>"
        );
      })
      .join("");
    return (
      '<div class="figures">' +
      '<div class="figures-label">Referenced figure' +
      (figures.length > 1 ? "s" : "") +
      "</div>" +
      items +
      "</div>"
    );
  }

  function init() {
    if (window.TecvanChat && window.TecvanChat.__mounted) return; // prevent double-mount

    var config = readConfig();
    if (!config.apiUrl) {
      console.error(
        "[Tecvan Chat Widget] apiUrl is required. Set window.TecvanChatConfig.apiUrl or data-api-url on the script tag."
      );
      return;
    }

    var host = document.createElement("div");
    host.id = "tecvan-chat-widget";
    host.style.cssText = "all: initial;";
    host.style.setProperty("--tv-brand", config.brandColor);
    host.style.setProperty("--tv-brand2", config.brandColor2);
    document.body.appendChild(host);

    var shadow = host.attachShadow({ mode: "open" });
    var styleEl = document.createElement("style");
    styleEl.textContent = CSS;
    shadow.appendChild(styleEl);

    var wrapper = document.createElement("div");
    wrapper.innerHTML = TEMPLATE;
    while (wrapper.firstChild) shadow.appendChild(wrapper.firstChild);

    var btn = shadow.querySelector(".btn");
    var iconChat = shadow.querySelector(".icon-chat");
    var iconClose = shadow.querySelector(".icon-close");
    var panel = shadow.querySelector(".panel");
    var headerAvatar = shadow.querySelector(".header .avatar");
    var headerName = shadow.querySelector(".header .name");
    var closeBtn = shadow.querySelector(".close");
    var minimizeBtn = shadow.querySelector(".minimize");
    var messagesEl = shadow.querySelector(".messages");
    var textarea = shadow.querySelector(".input-row textarea");
    var sendBtn = shadow.querySelector(".send");
    var generalFeedbackBtn = shadow.querySelector(".general-feedback-btn");
    var modalOverlay = shadow.querySelector(".modal-overlay");
    var modalTextarea = shadow.querySelector(".modal-textarea");
    var modalSendBtn = shadow.querySelector(".modal-send");
    var modalCancelBtn = shadow.querySelector(".modal-cancel");

    headerAvatar.textContent = config.avatarLabel;
    headerName.textContent = config.botName;
    var btnLabel = shadow.querySelector(".btn-label");
    if (btnLabel) btnLabel.textContent = config.launcherLabel;
    textarea.placeholder = config.inputPlaceholder;

    var messages = [];
    var loading = false;
    var streaming = false;
    var open = false;
    var conversationId = null;
    // Smooth streaming: tokens accumulate in streamTarget; an rAF loop copies
    // a growing prefix into messages[idx].content (3-8 chars/frame ≈ 200 cps),
    // so the UI animates smoothly instead of jumping per SSE delta.
    var streamTarget = "";
    var streamRevealed = 0;
    var streamRevealActive = false;
    var streamMsgIdx = -1;
    // Per-message feedback state, keyed by message.id:
    //   { rated: 'up'|'down'|null, commentOpen: bool, commentDraft: string, commentSent: bool }
    var feedbackState = {};
    // Conversation-level feedback: after a couple of answers we show a prominent
    // inline up/down prompt in the flow (non-blocking). Set once the shopper votes.
    var convFeedbackGiven = false;
    var CONV_FEEDBACK_AFTER = 2; // assistant answers before prompting
    // Live support handoff: under each bot answer, a link to email the whole
    // session to support. Session-level once sent (hide the prompt afterwards).
    var supportSent = false;
    var supportSentEmail = "";
    var supportSentMsgId = null;
    var supportOpenFor = null; // message id whose email form is open (transient)
    var supportSending = false;

    function ensureConversationId() {
      if (!conversationId) conversationId = uuid();
      return conversationId;
    }

    // ── Session persistence (minimize keeps it; close clears it) ──
    // Stored in localStorage so the conversation survives closing the box AND
    // navigating between storefront pages. Expires after SESSION_MAX_AGE.
    var SESSION_KEY = "tecvanChatSession:" + (config.botId || "tecvan");
    var SESSION_MAX_AGE = 6 * 60 * 60 * 1000; // 6 hours

    function saveSession() {
      try {
        if (!messages.length) { localStorage.removeItem(SESSION_KEY); return; }
        localStorage.setItem(SESSION_KEY, JSON.stringify({
          conversationId: conversationId, messages: messages, feedbackState: feedbackState,
          convFeedbackGiven: convFeedbackGiven, supportSent: supportSent,
          supportSentEmail: supportSentEmail, supportSentMsgId: supportSentMsgId, savedAt: Date.now()
        }));
      } catch (e) { /* storage unavailable/full — degrade to in-memory */ }
    }

    function loadSession() {
      try {
        var raw = localStorage.getItem(SESSION_KEY);
        if (!raw) return;
        var s = JSON.parse(raw);
        if (!s || !Array.isArray(s.messages) || (Date.now() - (s.savedAt || 0)) > SESSION_MAX_AGE) {
          localStorage.removeItem(SESSION_KEY); return;
        }
        messages = s.messages;
        conversationId = s.conversationId || null;
        feedbackState = s.feedbackState || {};
        convFeedbackGiven = !!s.convFeedbackGiven;
        supportSent = !!s.supportSent;
        supportSentEmail = s.supportSentEmail || "";
        supportSentMsgId = s.supportSentMsgId || null;
      } catch (e) { /* corrupt/unavailable — start fresh */ }
    }

    function clearSession() {
      messages = [];
      feedbackState = {};
      convFeedbackGiven = false;
      supportSent = false;
      supportSentEmail = "";
      supportSentMsgId = null;
      supportOpenFor = null;
      conversationId = null;
      try { localStorage.removeItem(SESSION_KEY); } catch (e) { /* ignore */ }
    }

    loadSession();

    function snapshotMessages() {
      return messages.map(function (m) {
        return { role: m.role, content: m.content };
      });
    }

    async function postFeedback(payload) {
      if (!config.apiUrl) return;
      try {
        // Feedback goes to the same chat endpoint (a Function URL behind
        // CloudFront OAC), distinguished by type:"feedback". POSTs must carry the
        // body hash in x-amz-content-sha256 for OAC signing.
        payload.type = "feedback";
        payload.device_fp = await deviceFingerprint();
        var bodyStr = JSON.stringify(payload);
        var amzHash = await sha256Hex(bodyStr);
        fetch(config.apiUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", "x-amz-content-sha256": amzHash },
          body: bodyStr,
          keepalive: true
        }).catch(function (e) {
          console.error("[Tecvan Chat Widget] feedback POST failed:", e);
        });
      } catch (e) {
        console.error("[Tecvan Chat Widget] feedback POST threw:", e);
      }
    }

    // Email the whole session to support (via the serving endpoint). Awaits the
    // SSE response so we can confirm success/failure to the shopper.
    async function postSupportRequest(email) {
      var payload = {
        type: "support_request",
        email: email,
        conversation_id: ensureConversationId(),
        messages: snapshotMessages(),
        page_context: detectPageContext(config),
        device_fp: await deviceFingerprint()
      };
      var bodyStr = JSON.stringify(payload);
      var amzHash = await sha256Hex(bodyStr);
      var res = await fetch(config.apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-amz-content-sha256": amzHash },
        body: bodyStr
      });
      var text = await res.text();
      if (/"type"\s*:\s*"support_ok"/.test(text)) return;
      var m = text.match(/"type"\s*:\s*"support_error"[\s\S]*?"content"\s*:\s*"([^"]*)"/);
      throw new Error(m ? m[1] : "We couldn't send that. Please email support@tecvan.com directly.");
    }

    function buildFeedbackPayload(opts) {
      // opts: { messageId, ratedIndex, feedbackType, rating, comment }
      return {
        conversation_id: ensureConversationId(),
        message_id: opts.messageId,
        bot_id: config.botId,
        timestamp: new Date().toISOString(),
        feedback_type: opts.feedbackType,
        rating: opts.rating || null,
        comment: opts.comment || null,
        messages_snapshot: snapshotMessages(),
        rated_message_index: opts.ratedIndex == null ? null : opts.ratedIndex,
        page_url: location.href,
        user_agent: navigator.userAgent,
        widget_version: WIDGET_VERSION,
        source: "embed"
      };
    }

    function syncCommentDrafts() {
      // Preserve in-progress comment text across re-renders.
      var nodes = messagesEl.querySelectorAll("[data-comment-textarea]");
      for (var i = 0; i < nodes.length; i++) {
        var id = nodes[i].getAttribute("data-comment-textarea");
        if (feedbackState[id]) feedbackState[id].commentDraft = nodes[i].value;
      }
    }

    function renderFeedbackRow(m, msgIdx) {
      var st = feedbackState[m.id] || {};
      var rated = st.rated || null;
      var row = document.createElement("div");
      row.className = "feedback-row";
      row.innerHTML =
        '<button class="feedback-btn up' +
        (rated === "up" ? " active" : "") +
        '" type="button" aria-label="Helpful"' +
        (rated ? " disabled" : "") +
        ' data-action="rate-up" data-msg-id="' +
        escapeHtml(m.id) +
        '" data-msg-idx="' +
        msgIdx +
        '">' +
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="' +
        (rated === "up" ? "currentColor" : "none") +
        '" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 11v9a1 1 0 0 0 1 1h9.5a2 2 0 0 0 2-1.7l1.4-7A2 2 0 0 0 19 10h-5l1-4a2 2 0 0 0-2-2.5L8 11z"/><path d="M3 11h4v10H3z"/></svg>' +
        "</button>" +
        '<button class="feedback-btn down' +
        (rated === "down" ? " active" : "") +
        '" type="button" aria-label="Not helpful"' +
        (rated ? " disabled" : "") +
        ' data-action="rate-down" data-msg-id="' +
        escapeHtml(m.id) +
        '" data-msg-idx="' +
        msgIdx +
        '">' +
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="' +
        (rated === "down" ? "currentColor" : "none") +
        '" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 13V4a1 1 0 0 0-1-1H6.5a2 2 0 0 0-2 1.7l-1.4 7A2 2 0 0 0 5 14h5l-1 4a2 2 0 0 0 2 2.5L16 13z"/><path d="M21 13h-4V3h4z"/></svg>' +
        "</button>" +
        (st.commentSent ? '<span class="feedback-thanks">Thanks for your feedback.</span>' : "");
      return row;
    }

    function renderCommentBox(m, msgIdx) {
      var st = feedbackState[m.id] || {};
      var draft = st.commentDraft || "";
      var box = document.createElement("div");
      box.className = "feedback-comment";
      box.innerHTML =
        '<textarea data-comment-textarea="' +
        escapeHtml(m.id) +
        '" placeholder="What was wrong with this answer? (optional)"></textarea>' +
        '<div class="row">' +
        '<button type="button" data-action="comment-cancel" data-msg-id="' +
        escapeHtml(m.id) +
        '">No thanks</button>' +
        '<button type="button" class="primary" data-action="comment-send" data-msg-id="' +
        escapeHtml(m.id) +
        '" data-msg-idx="' +
        msgIdx +
        '">Send</button>' +
        "</div>";
      // textContent assignment preserves any characters the user typed
      var ta = box.querySelector("textarea");
      ta.value = draft;
      return box;
    }

    function renderConvFeedback() {
      var box = document.createElement("div");
      box.className = "conv-feedback";
      if (convFeedbackGiven) {
        box.className += " done";
        box.innerHTML = '<span class="conv-feedback-thanks">Thanks for your feedback! 🙏</span>';
        return box;
      }
      box.innerHTML =
        '<span class="conv-feedback-q">Is this conversation helping?</span>' +
        '<div class="conv-feedback-btns">' +
        '<button type="button" class="conv-feedback-btn up" data-action="conv-rate-up" data-msg-id="conversation" aria-label="Yes, helpful">👍 Yes</button>' +
        '<button type="button" class="conv-feedback-btn down" data-action="conv-rate-down" data-msg-id="conversation" aria-label="No, not helpful">👎 No</button>' +
        "</div>";
      return box;
    }

    // Per-answer support handoff: a link, an inline email form, or (once sent)
    // a confirmation. Returns null to render nothing under this message.
    function renderSupportEl(m) {
      var wrap = document.createElement("div");
      if (supportSent) {
        if (m.id !== supportSentMsgId) return null;
        wrap.className = "support-row";
        wrap.innerHTML = '<span class="support-sent">✓ Thanks — our team will email you at ' + escapeHtml(supportSentEmail) + ".</span>";
        return wrap;
      }
      if (supportOpenFor === m.id) {
        wrap.className = "support-form";
        wrap.innerHTML =
          '<div class="support-form-label">Leave your email and our team will follow up.</div>' +
          '<div class="support-form-row">' +
          '<input type="email" class="support-email-input" data-support-email placeholder="you@email.com" />' +
          '<button type="button" class="support-btn primary" data-action="support-send" data-msg-id="' + escapeHtml(m.id) + '">Send</button>' +
          '<button type="button" class="support-btn" data-action="support-cancel" data-msg-id="' + escapeHtml(m.id) + '">Cancel</button>' +
          "</div>" +
          '<div class="support-err" data-support-err></div>';
        return wrap;
      }
      wrap.className = "support-row";
      wrap.innerHTML = '<button type="button" class="support-link" data-action="support-open" data-msg-id="' + escapeHtml(m.id) + '">Didn’t answer your question? Talk to our team →</button>';
      return wrap;
    }

    function renderMessages() {
      syncCommentDrafts();
      messagesEl.innerHTML = "";
      if (messages.length === 0) {
        var welcome = document.createElement("div");
        welcome.className = "msg bot";
        welcome.innerHTML =
          '<div class="avatar-sm">' +
          escapeHtml(config.avatarLabel) +
          '</div><div class="bubble">' +
          renderInline(config.welcomeMessage) +
          "</div>";
        messagesEl.appendChild(welcome);
      } else {
        messages.forEach(function (m, mi) {
          var node = document.createElement("div");
          node.className = "msg " + (m.role === "user" ? "user" : "bot");
          if (m.role === "user") {
            node.innerHTML = '<div class="bubble">' + escapeHtml(m.content) + "</div>";
          } else {
            var visible = stripAttachTags(m.content);
            var isLastForFigs = mi === messages.length - 1;
            var showFigures = m.figures && m.figures.length > 0 && !(streaming && isLastForFigs);
            var showProducts = m.products && m.products.length > 0 && !(streaming && isLastForFigs);
            node.innerHTML =
              '<div class="avatar-sm">' +
              escapeHtml(config.avatarLabel) +
              '</div><div class="bubble">' +
              renderInline(visible) +
              (showFigures ? renderFiguresHtml(m.figures) : "") +
              (showProducts ? renderProductsHtml(m.products) : "") +
              "</div>";
          }
          messagesEl.appendChild(node);
          var isLast = mi === messages.length - 1;
          var canRate =
            m.role === "assistant" &&
            !!stripAttachTags(m.content) &&
            !(streaming && isLast);
          if (canRate) {
            messagesEl.appendChild(renderFeedbackRow(m, mi));
            var st = feedbackState[m.id];
            if (st && st.commentOpen) {
              messagesEl.appendChild(renderCommentBox(m, mi));
            }
            var supEl = renderSupportEl(m);
            if (supEl) messagesEl.appendChild(supEl);
          }
        });

        // Conversation-level prompt: once the bot has answered a couple of times,
        // show a prominent (but non-blocking) up/down in the flow so more shoppers
        // actually rate the conversation. Hidden once they vote.
        var answerCount = messages.filter(function (m) {
          return m.role === "assistant" && !!stripAttachTags(m.content);
        }).length;
        if (!streaming && !loading && answerCount >= CONV_FEEDBACK_AFTER) {
          messagesEl.appendChild(renderConvFeedback());
        }
      }
      if (
        loading &&
        (messages.length === 0 || messages[messages.length - 1].role !== "assistant")
      ) {
        var typing = document.createElement("div");
        typing.className = "msg bot";
        typing.innerHTML =
          '<div class="avatar-sm">' +
          escapeHtml(config.avatarLabel) +
          '</div><div class="typing"><span></span><span></span><span></span></div>';
        messagesEl.appendChild(typing);
      }
      messagesEl.scrollTop = messagesEl.scrollHeight;
    }

    function setOpen(next) {
      open = next;
      panel.hidden = !open;
      btn.setAttribute("aria-label", open ? "Close chat" : "Open chat");
      btn.classList.toggle("is-open", open); // pill collapses to a circle when open
      iconChat.style.display = open ? "none" : "";
      iconClose.style.display = open ? "" : "none";
      if (open) {
        renderMessages();
        setTimeout(function () {
          textarea.focus();
        }, 0);
      }
    }

    function updateSendState() {
      sendBtn.disabled = !textarea.value.trim() || loading;
    }

    function setLoading(v) {
      loading = v;
      updateSendState();
      renderMessages();
    }

    function startRevealLoop() {
      if (streamRevealActive) return;
      streamRevealActive = true;
      function tick() {
        if (!streamRevealActive) return;
        if (streamRevealed < streamTarget.length && streamMsgIdx >= 0 && messages[streamMsgIdx]) {
          var remaining = streamTarget.length - streamRevealed;
          var step = Math.max(3, Math.min(8, Math.ceil(remaining * 0.08)));
          streamRevealed = Math.min(streamRevealed + step, streamTarget.length);
          messages[streamMsgIdx].content = streamTarget.slice(0, streamRevealed);
          renderMessages();
        }
        requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    }

    function stopRevealLoop() {
      streamRevealActive = false;
      if (streamMsgIdx >= 0 && messages[streamMsgIdx] && streamTarget) {
        messages[streamMsgIdx].content = streamTarget;
        renderMessages();
      }
      streamTarget = "";
      streamRevealed = 0;
      streamMsgIdx = -1;
    }

    async function send() {
      var text = textarea.value.trim();
      if (!text || loading) return;
      textarea.value = "";
      ensureConversationId();
      messages.push({ id: uuid(), role: "user", content: text });
      saveSession();
      setLoading(true);

      try {
        // Detect the current Shopify page scope; fetch related products on a
        // product page (lazy, short timeout, cached). Failures degrade to no scope.
        var pageContext = detectPageContext(config);
        if (pageContext.scope === "product") {
          try {
            pageContext.related_product_handles = await fetchRelated(pageContext);
          } catch (e) { /* ignore — scope still works without related */ }
        }
        var requestBody = JSON.stringify({
          botId: config.botId,
          messages: messages,
          conversation_id: ensureConversationId(),
          source: "shopify_embed",
          store_domain: config.storeDomain,
          page_context: pageContext,
          device_fp: await deviceFingerprint()
        });
        // The chat endpoint is a Lambda Function URL behind CloudFront with
        // Origin Access Control, which signs each request. For POSTs, the body
        // hash MUST be supplied in x-amz-content-sha256 or the origin rejects it.
        var amzHash = await sha256Hex(requestBody);
        var res = await fetch(config.apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-amz-content-sha256": amzHash
          },
          body: requestBody
        });

        var ct = res.headers.get("Content-Type") || "";
        if (ct.indexOf("text/event-stream") !== -1 && res.body && res.body.getReader) {
          var reader = res.body.getReader();
          var decoder = new TextDecoder();
          var buffer = "";
          messages.push({ id: uuid(), role: "assistant", content: "" });
          var idx = messages.length - 1;
          streaming = true;
          setLoading(false);

          // Hand the assistant slot to the rAF reveal loop.
          streamTarget = "";
          streamRevealed = 0;
          streamMsgIdx = idx;
          startRevealLoop();

          while (true) {
            var chunk = await reader.read();
            if (chunk.done) break;
            buffer += decoder.decode(chunk.value, { stream: true });
            var lines = buffer.split("\n");
            buffer = lines.pop() || "";
            for (var i = 0; i < lines.length; i++) {
              var line = lines[i];
              if (line.indexOf("data: ") !== 0) continue;
              try {
                var evt = JSON.parse(line.slice(6));
                if (evt.type === "token" && evt.content) {
                  streamTarget += evt.content;
                } else if (evt.type === "attached_figures") {
                  messages[idx].figures = Array.isArray(evt.figures) ? evt.figures : [];
                } else if (evt.type === "products") {
                  messages[idx].products = Array.isArray(evt.items) ? evt.items : [];
                } else if (evt.type === "meta") {
                  messages[idx].meta = evt;
                }
              } catch (e) {
                // skip malformed events
              }
            }
          }

          stopRevealLoop();
          if (!messages[idx].content) {
            messages[idx].content = "Sorry, I couldn't generate a response.";
          }
          streaming = false;
          renderMessages();
          saveSession();
        } else {
          var data;
          try {
            data = await res.json();
          } catch (e) {
            data = null;
          }
          var reply =
            (data && (data.reply || data.message || data.response)) ||
            "Sorry, I couldn't generate a response.";
          var figs = data && Array.isArray(data.figures) ? data.figures : [];
          messages.push({ id: uuid(), role: "assistant", content: reply, figures: figs });
          setLoading(false);
        }
      } catch (e) {
        console.error("[Tecvan Chat Widget] request error:", e);
        streaming = false;
        messages.push({
          id: uuid(),
          role: "assistant",
          content: "Sorry, something went wrong. Please try again."
        });
        setLoading(false);
      }
    }

    btn.addEventListener("click", function () {
      setOpen(!open);
    });
    // Minimize: hide the box but KEEP the conversation (persisted) so reopening
    // — even after navigating to another page — resumes where they left off.
    minimizeBtn.addEventListener("click", function () {
      saveSession();
      setOpen(false);
    });
    // Close: END the conversation — clear it so reopening starts fresh.
    closeBtn.addEventListener("click", function () {
      clearSession();
      renderMessages();
      setOpen(false);
    });
    sendBtn.addEventListener("click", send);
    textarea.addEventListener("input", updateSendState);
    textarea.addEventListener("keydown", function (e) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        send();
      }
    });

    messagesEl.addEventListener("click", function (e) {
      var target = e.target;
      while (target && target !== messagesEl && !target.getAttribute("data-action")) {
        target = target.parentNode;
      }
      if (!target || target === messagesEl) return;
      var action = target.getAttribute("data-action");
      var msgId = target.getAttribute("data-msg-id");
      var msgIdxAttr = target.getAttribute("data-msg-idx");
      var msgIdx = msgIdxAttr == null ? null : parseInt(msgIdxAttr, 10);
      if (!msgId) return;

      if (action === "support-open") {
        supportOpenFor = msgId;
        renderMessages();
        var inp = messagesEl.querySelector("[data-support-email]");
        if (inp) inp.focus();
        return;
      }
      if (action === "support-cancel") {
        supportOpenFor = null;
        renderMessages();
        return;
      }
      if (action === "support-send") {
        var input = messagesEl.querySelector("[data-support-email]");
        var errEl = messagesEl.querySelector("[data-support-err]");
        var email = input ? input.value.trim() : "";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          if (errEl) errEl.textContent = "Please enter a valid email.";
          return;
        }
        if (errEl) errEl.textContent = "";
        target.textContent = "Sending…";
        target.disabled = true;
        if (input) input.disabled = true;
        postSupportRequest(email).then(function () {
          supportSent = true;
          supportSentEmail = email;
          supportSentMsgId = msgId;
          supportOpenFor = null;
          saveSession();
          renderMessages();
        }).catch(function (e) {
          if (errEl) errEl.textContent = (e && e.message) || "Couldn't send. Please try again.";
          target.textContent = "Send";
          target.disabled = false;
          if (input) input.disabled = false;
        });
        return;
      }

      if (action === "conv-rate-up" || action === "conv-rate-down") {
        if (convFeedbackGiven) return;
        var convRating = action === "conv-rate-up" ? "up" : "down";
        convFeedbackGiven = true;
        postFeedback(
          buildFeedbackPayload({
            messageId: "conversation",
            ratedIndex: null,
            feedbackType: "conversation",
            rating: convRating
          })
        );
        saveSession();
        renderMessages();
        return;
      }

      if (action === "rate-up" || action === "rate-down") {
        if (feedbackState[msgId] && feedbackState[msgId].rated) return;
        var rating = action === "rate-up" ? "up" : "down";
        feedbackState[msgId] = {
          rated: rating,
          commentOpen: rating === "down",
          commentDraft: "",
          commentSent: false
        };
        postFeedback(
          buildFeedbackPayload({
            messageId: msgId,
            ratedIndex: msgIdx,
            feedbackType: "rating",
            rating: rating
          })
        );
        renderMessages();
      } else if (action === "comment-cancel") {
        if (feedbackState[msgId]) {
          feedbackState[msgId].commentOpen = false;
          feedbackState[msgId].commentDraft = "";
        }
        renderMessages();
      } else if (action === "comment-send") {
        var ta = messagesEl.querySelector('[data-comment-textarea="' + msgId + '"]');
        var commentText = ta ? ta.value.trim() : "";
        if (!feedbackState[msgId]) feedbackState[msgId] = {};
        feedbackState[msgId].commentOpen = false;
        feedbackState[msgId].commentDraft = "";
        feedbackState[msgId].commentSent = true;
        if (commentText) {
          postFeedback(
            buildFeedbackPayload({
              messageId: msgId,
              ratedIndex: msgIdx,
              feedbackType: "comment",
              rating: feedbackState[msgId].rated || null,
              comment: commentText
            })
          );
        }
        renderMessages();
      }
    });

    function openFeedbackModal() {
      modalTextarea.value = "";
      modalOverlay.hidden = false;
      setTimeout(function () {
        modalTextarea.focus();
      }, 0);
    }
    function closeFeedbackModal() {
      modalOverlay.hidden = true;
    }
    generalFeedbackBtn.addEventListener("click", openFeedbackModal);
    modalCancelBtn.addEventListener("click", closeFeedbackModal);
    modalSendBtn.addEventListener("click", function () {
      var text = modalTextarea.value.trim();
      if (!text) {
        closeFeedbackModal();
        return;
      }
      postFeedback(
        buildFeedbackPayload({
          messageId: "general",
          ratedIndex: null,
          feedbackType: "general",
          rating: null,
          comment: text
        })
      );
      closeFeedbackModal();
    });
    modalOverlay.addEventListener("click", function (e) {
      if (e.target === modalOverlay) closeFeedbackModal();
    });

    window.TecvanChat = {
      __mounted: true,
      open: function () {
        setOpen(true);
      },
      close: function () {
        setOpen(false);
      },
      toggle: function () {
        setOpen(!open);
      },
      reset: function () {
        clearSession();
        renderMessages();
      },
      config: config
    };
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
